import React, { useRef, useCallback } from 'react';
import { LinkType, ILink } from './../../../services/types/index';
export enum LinkActionType {
  Init = 'Init',
  SetType = 'SetType',
  SetTitle = 'SetTitle',
  SetLabel = 'SetLabel',
  SetDescription = 'SetDescription',
  SetUrl = 'SetUrl',
  SetCoverUrl = 'SetCoverUrl',
}

export interface LinkAction {
  type: LinkActionType;
  payload: Partial<ILink>;
}

const initialState: ILink = {
  type: LinkType.Internal,
  title: '',
  label: '',
  desc: '',
  url: '',
  coverUrl: '',
};

const linkReducer = (state: ILink, action: LinkAction): ILink => {
  const { type, payload } = action;

  switch (type) {
    case LinkActionType.Init:
    case LinkActionType.SetType:
    case LinkActionType.SetTitle:
    case LinkActionType.SetLabel:
    case LinkActionType.SetDescription:
    case LinkActionType.SetUrl:
    case LinkActionType.SetCoverUrl:
      return { ...state, ...payload };

    default:
      throw new Error();
  }
};

const useLink = ({ reducer = linkReducer, init = initialState } = {}) => {
  const [link, dispatch] = React.useReducer(reducer, init);

  const currentDefaultLink = useRef<ILink>(init);

  const initialize = useCallback((value?: ILink) => {
    if (value) {
      currentDefaultLink.current = value;
    }

    dispatch({
      type: LinkActionType.Init,
      payload: currentDefaultLink.current,
    });
  }, []);

  const getDefault = useCallback(() => currentDefaultLink.current, []);

  const setType = (type: LinkType) =>
    dispatch({
      type: LinkActionType.SetType,
      payload: {
        type,
      },
    });

  const setTitle = (title: string) =>
    dispatch({
      type: LinkActionType.SetTitle,
      payload: {
        title,
      },
    });

  const setLabel = (label: string) =>
    dispatch({
      type: LinkActionType.SetLabel,
      payload: {
        label,
      },
    });

  const setDescription = (desc: string) =>
    dispatch({
      type: LinkActionType.SetDescription,
      payload: {
        desc,
      },
    });

  const setUrl = (url: string) =>
    dispatch({
      type: LinkActionType.SetUrl,
      payload: {
        url,
      },
    });

  const setCoverUrl = (coverUrl: string) =>
    dispatch({
      type: LinkActionType.SetCoverUrl,
      payload: {
        coverUrl,
      },
    });

  return { link, setType, setTitle, setLabel, setDescription, setUrl, setCoverUrl, initialize, getDefault };
};

export { useLink };
